/* eslint-disable max-len */
import { DataEntryMode } from '../DataEntryMode';
import { VALIDATION_MESSAGE } from '../ValidationMessage';
import { createDateSchema } from './CustomValidation/DateExtendedSchema';
import yup from './CustomValidation/yup-extended';
import { ExtendedDateSchemaType } from './ValidationTypes';

export const getExitAdjustmentValidationSchema = (
  dataEntryMode: DataEntryMode = DataEntryMode.NEW
): yup.AnyObjectSchema =>
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ((parseDateValue, getSchoolYearEndDate) =>
    ((mode) =>
      yup.object().shape({
        studentIdentifierState:
          mode !== DataEntryMode.EDIT
            ? yup
                .string()
                .required(VALIDATION_MESSAGE.REQUIRED)
                .mustBeNumber()
                .min(9, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(9))
                .max(9, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(9))
            : yup.string(),
        // Use .test() (not .required()) so labels do not show a *; FormInputLabel only treats yup "required" tests as required.
        studentLastOrSurname:
          mode !== DataEntryMode.EDIT
            ? yup
                .string()
                .test(
                  'exit-adjustment-last-name-from-lookup',
                  VALIDATION_MESSAGE.REQUIRED,
                  (value) => Boolean(value && String(value).trim())
                )
            : yup.string(),
        studentFirstName:
          mode !== DataEntryMode.EDIT
            ? yup
                .string()
                .test(
                  'exit-adjustment-first-name-from-lookup',
                  VALIDATION_MESSAGE.REQUIRED,
                  (value) => Boolean(value && String(value).trim())
                )
            : yup.string(),
        studentMiddleName: yup.string(),
        birthDate: yup.string(),
        removalReason: yup.string(),
        updatedExitType: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
        updatedExitDate: (
          createDateSchema() as ExtendedDateSchemaType
        ).required(VALIDATION_MESSAGE.REQUIRED),
        schoolStudentTransferredTo: yup
          .string()
          .max(300, VALIDATION_MESSAGE.AT_MOST_CHARS(300)),
        documentsUploadedToSharePoint: yup
          .boolean()
          .required(VALIDATION_MESSAGE.REQUIRED)
          .oneOf([true], VALIDATION_MESSAGE.REQUIRED),
        denialReason: yup
          .string()
          .when('reviewStatus', {
            is: (reviewStatus: string | undefined) => reviewStatus === 'Denied',
            then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
            otherwise: yup.string().notRequired(),
          })
          .max(1000, VALIDATION_MESSAGE.AT_MOST_CHARS(1000))
          .matches(/^[a-zA-Z0-9\s./-]+$/, {
            message:
              VALIDATION_MESSAGE.MUST_BE_LETTERS_NUMBERS_PERIOD_HYPHEN_OR_SLASH,
            excludeEmptyString: true,
          }),
      }))(`${String(dataEntryMode).toLowerCase()}` as DataEntryMode))(
    (value: unknown): Date | null => {
      if (!value) return null;
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }
      if (typeof value === 'string') {
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoMatch) {
          const parsedIsoDate = new Date(
            Number(isoMatch[1]),
            Number(isoMatch[2]) - 1,
            Number(isoMatch[3])
          );
          return Number.isNaN(parsedIsoDate.getTime()) ? null : parsedIsoDate;
        }

        const slashOrDashMatch = value.match(
          /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/
        );
        if (slashOrDashMatch) {
          const parsedSlashOrDashDate = new Date(
            Number(slashOrDashMatch[3]),
            Number(slashOrDashMatch[1]) - 1,
            Number(slashOrDashMatch[2])
          );
          return Number.isNaN(parsedSlashOrDashDate.getTime())
            ? null
            : parsedSlashOrDashDate;
        }
      }
      return null;
    },
    (schoolYear: unknown): Date | null => {
      if (typeof schoolYear !== 'string') return null;
      const yearMatch = schoolYear.match(/\d{4}/);
      if (!yearMatch) return null;
      const endYear = Number(yearMatch[0]);
      const schoolYearEnd = new Date(endYear, 5, 30);
      return Number.isNaN(schoolYearEnd.getTime()) ? null : schoolYearEnd;
    }
  );

export const ExitAdjustmentValidationSchema = getExitAdjustmentValidationSchema(
  DataEntryMode.NEW
);
